import h from 'hyperscript';

import { createButton } from 'ui/button/button';
import { Alerts } from 'ui/alerts/alerts';
import { DateRangeChangeEvent, DateRangePicker } from 'ui/date-range-picker/date-range-picker';

import { on } from 'cadenza/utils/event-util';
import { TickUnit } from 'cadenza/conditions/model/tick-unit';
import {
  type AnimationSettingResult,
  AnimationSettingsDialog
} from 'cadenza/conditions/actions/animation-settings-dialog';
import type { StepsConfig } from 'cadenza/conditions/date-time-condition/steps-config';
import { DateAnimationPreview } from 'cadenza/conditions/date-time-condition/date-animation-preview';
import { SliderStepsConfig, STEPS_CONFIG_CHANGE } from 'cadenza/conditions/date-time-condition/slider-steps-config';
import { calculateAutomaticFrameDistribution } from 'cadenza/conditions/animation/automatic-frame-distribution-utils';
import { assert } from 'cadenza/utils/custom-error';

import i18n from './date-animation-settings-dialog.properties';
import './date-animation-settings-dialog.css';

const COMPONENT_NAME = 'd-date-animation-settings-dialog';

export interface DateAnimationSettingsDialogOptions {
  initialStepsConfig: StepsConfig;
  dateFormat: string;
  smallestUnit: TickUnit;
  defaultStart: Date;
  customStart: Date;
  defaultEnd: Date;
  customEnd: Date;
  maxFrames: number;
  pointInTimeMode: boolean;
}

export class DateAnimationSettingsDialog extends AnimationSettingsDialog {

  #options: DateAnimationSettingsDialogOptions;
  #customStart: Date;
  #customEnd: Date;

  _animationRangePicker: DateRangePicker;
  _preview: DateAnimationPreview;
  _stepsConfigPanel?: SliderStepsConfig;
  _alerts: Alerts;
  _automaticDistributionAlerts: Alerts;

  constructor (options: DateAnimationSettingsDialogOptions) {
    super();
    this.#options = options;
    this.#customStart = options.customStart;
    this.#customEnd = options.customEnd;

    this._animationRangePicker = this._createAndListenToRangePicker();
    this._preview = this._createPreview(options.initialStepsConfig);
    if (!options.pointInTimeMode) {
      this._stepsConfigPanel = this._createAndListenToStepsConfigPanel();
    }
    this._alerts = this._createAlerts();
    this._automaticDistributionAlerts = this._createAlerts();
    this._updateAlerts();
  }

  createSpecificContent (): HTMLElement[] {
    if (this.#options.pointInTimeMode) {
      return [
        this._preview,
        this._alerts
      ];
    } else {
      return [
        this._createAutomaticDistribution(),
        this._preview,
        this._stepsConfigPanel!, // there is always a _stepConfigPanel if we are not in point-in-time mode
        this._alerts
      ];
    }
  }

  _createAndListenToRangePicker () {
    const rangePicker = new DateRangePicker({
      initialStartDate: this.#customStart,
      initialEndDate: this.#customEnd,
      minDate: this.#options.defaultStart,
      maxDate: this.#options.defaultEnd,
      dateFormat: this.#options.dateFormat,
      mostFineGrainedTickUnit: this.#options.smallestUnit,
      popupMode: true
    });
    rangePicker.classList.add('animation-visualization--range-picker');
    on(rangePicker, DateRangeChangeEvent.type, (event: DateRangeChangeEvent) => {
      assert(event instanceof CustomEvent, 'event must be an instance of CustomEvent.');
      let effectiveValue;
      if (event.detail.isStart) {
        effectiveValue = event.detail.value ?? this.#options.defaultStart;
        this.#customStart = effectiveValue;
      } else {
        effectiveValue = event.detail.value ?? this.#options.defaultEnd;
        this.#customEnd = effectiveValue;
      }
      this._stepsConfigPanel?.updateDateRange(event.detail.isStart, effectiveValue);
      this._updatePreview();
    });
    return rangePicker;
  }

  _createPreview (stepsConfig: StepsConfig): DateAnimationPreview {
    return new DateAnimationPreview(
      stepsConfig,
      this.#options.smallestUnit,
      this._effectiveStart(),
      this._effectiveEnd(),
      this.#options.maxFrames,
      this._animationRangePicker);
  }

  _createAndListenToStepsConfigPanel (): SliderStepsConfig {
    const stepsConfigPanel = new SliderStepsConfig(
      this.#options.initialStepsConfig,
      this.#options.smallestUnit,
      this._effectiveStart(),
      this._effectiveEnd()
    );
    stepsConfigPanel.addEventListener(STEPS_CONFIG_CHANGE, () => this._updatePreview());
    return stepsConfigPanel;
  }

  _createAlerts () {
    const alerts = new Alerts();
    alerts.classList.add('d-date-animation-settings-dialog--alerts');
    alerts.compactMessages = true;
    return alerts;
  }

  _updateAlerts () {
    this._alerts.alerts = [];
    const stepsConfig = this._currentStepsConfig();

    if (this._preview?.hasLessThanTwoFrames()) {
      this._alerts.alerts = [ {
        title: '',
        message: i18n('alert.onlyOneFrame'),
        type: 'warning'
      } ];
    } else if (this._preview?.hasOverset()) {
      this._alerts.alerts = [ {
        title: '',
        message: i18n(
          'alert.overset',
          {
            window: stepsConfig.timeWindow + ' ' + TickUnit.i18n(stepsConfig.unit, { plural: stepsConfig.timeWindow !== 1 })
          }),
        type: 'warning'
      } ];
    } else {
      this._alerts.alerts = [];
    }
  }

  _updateAutomaticFrameDistributionAlerts (message: string | undefined) {
    this._automaticDistributionAlerts.alerts = message
      ? [ { title: '', message, type: 'warning' } ]
      : [];
  }

  _effectiveStart () {
    return this.#customStart ?? this.#options.defaultStart;
  }

  _effectiveEnd () {
    return this.#customEnd ?? this.#options.defaultEnd;
  }

  _updatePreview () {
    if (this._preview) {
      const updatedPreview = this._createPreview(this._currentStepsConfig());
      this._preview.replaceWith(updatedPreview);
      this._preview = updatedPreview;
      this._updateAlerts();
    }
  }

  _calculateAutomaticFrameDistribution () {
    const result = calculateAutomaticFrameDistribution(
      this.#options.maxFrames,
      this._effectiveStart(),
      this._effectiveEnd(),
      this.#options.smallestUnit);
    this._stepsConfigPanel?.setStepsConfig(result.stepsConfig);
    this._updatePreview();
    this._updateAutomaticFrameDistributionAlerts(result?.message);
  }

  _createAutomaticDistribution () {
    return h('.form-group', [
      h('label', i18n('settingsSubheading')),
      h('p', i18n('animation.settings.automatic-distribution')),
      createButton(i18n('animation.settings.generate.automatic-distribution'), {
        onclick: () => this._calculateAutomaticFrameDistribution()
      }),
      this._automaticDistributionAlerts
    ]);
  }

  _currentStepsConfig () {
    return this._stepsConfigPanel?.getStepsConfig() ?? this.#options.initialStepsConfig;
  }

  override onSubmit (event: SubmitEvent) {
    const result: AnimationSettingResult = {
      type: 'date',
      viewId: this.selectedViewId,
      ...this._currentStepsConfig(),
      rangeStart: this._effectiveStart().toISOString(),
      rangeEnd: this._effectiveEnd().toISOString(),
      sliderRangeStart: this._effectiveStart(),
      sliderRangeEnd: this._effectiveEnd()
    };
    this.close(result);
  }

}

customElements.define(COMPONENT_NAME, DateAnimationSettingsDialog);
